import Contrat from '@/generated/classes/Contrat';
import Etablissement from '@/generated/classes/Etablissement';
import Dialog from '@/generated/classes/Dialog';
import Route from '@/generated/classes/Route';
import { Fiches, Modules, Sections } from '@/generated/classes/AppSchema';
import { type ControleSignatureMasseManque } from '@/extend/classes/ResultatContratMasse';
import { ControleSignatureMasseManqueCodes, ControleSignatureMasseManqueEtapes } from '@/generated/classes/ResultatContratMasse';
import { Alerts } from '@/framework/base/helpers/Alerts';
import { openAlert } from '@/framework/base/components/SectionAlert.vue';
import { ouvrirEtAttendre } from '@/custom/helpers/attenteRoute';
import { titreModale, detailVentilation } from '@/custom/helpers/messagesMasse';
import { type EtapeControle, type Manque, type SourceControle } from '@/custom/helpers/signatureMasse';

// Étape « société » de l'envoi en signature en masse (OVA-4979).
//
// Le contrôle est fait par le serveur (`POST api/contrats/controleSignatureMasse`), avec les règles
// mêmes du parcours unitaire, partagées avec `CheckContrat` et `Submit` : identité (TVA, tranche
// d'effectif) et signataire (représentant et coordonnées). Le client ne recopie aucune règle ; il
// présente les manques et propose de les compléter.
//
// La complétion n'ajoute aucune modale de saisie : elle rouvre celles du parcours unitaire, avec les
// routes mêmes que construit le serveur. Les sections savent déjà n'afficher que les champs manquants.

/** Codes des manques renvoyés par le serveur. */
export const MANQUE_TVA = ControleSignatureMasseManqueCodes.IDENTITE_TVA;
export const MANQUE_EFFECTIF = ControleSignatureMasseManqueCodes.IDENTITE_EFFECTIF;
export const MANQUE_SIGNATAIRE_ABSENT = ControleSignatureMasseManqueCodes.SIGNATAIRE_ABSENT;
export const MANQUE_SIGNATAIRE_INCOMPLET = ControleSignatureMasseManqueCodes.SIGNATAIRE_INCOMPLET;

const ETAPE_SOCIETE = ControleSignatureMasseManqueEtapes.SOCIETE;

/**
 * Manques de périmètre (contrat introuvable, établissement illisible) : non complétables, ils écartent
 * leurs contrats dès cette première étape plutôt que de les laisser traverser les suivantes.
 */
const MANQUES_PERIMETRE = [ControleSignatureMasseManqueCodes.HORS_PERIMETRE, ControleSignatureMasseManqueCodes.ETABLISSEMENT_ILLISIBLE];

const MANQUES_IDENTITE = [MANQUE_TVA, MANQUE_EFFECTIF];

/** Attributs exigés du représentant, au format attendu par la section contact (cf. `Submit`). */
const ATTRIBUTS_SIGNATAIRE = ['CONTACT.CIVILITE_ID', 'CONTACT.QUALITE_ID', 'CONTACT.MAIL', 'CONTACT.NUMERO'];

const BOUTON_ANNULER = 'Annuler';
const BOUTON_CONTINUER = 'Continuer';

/** Route de modale, construite comme les `HttpRoute` du serveur (id porté par `sectionObjId`). */
function routeModale(module: string, fiche: string, section: string, sectionObjId: number): Route {
  const route = new Route();
  route.module = module;
  route.fiche = fiche;
  route.section = section;
  route.sectionObjId = sectionObjId;
  route.isDialog = true;
  return route;
}

/** Nom affichable d'une société, pour distinguer les messages quand la sélection en compte plusieurs. */
function nomSociete(etablissement: Etablissement): string {
  return etablissement.alias || etablissement.nom;
}

/**
 * Message d'introduction, au wording de l'US. Résout `true` si l'utilisateur choisit de continuer.
 *
 * Le bouton est « Continuer » et non « Enregistrer et continuer » : ce message ne porte aucun champ,
 * l'enregistrement se fait dans les modales qui suivent (écart avec l'US, plan §6.4).
 */
async function proposerCompletion(nombreContrats: number, libelles: string[], societe?: string): Promise<boolean> {
  const dialog = new Dialog();
  dialog.label = titreModale('Informations société à compléter')
    + `Vous avez sélectionné <strong>${nombreContrats} contrat(s)</strong>.<br>`
    + 'Certaines informations de la société employeuse'
    + (societe ? ` <strong>${societe}</strong>` : '')
    + ' sont nécessaires pour poursuivre la demande de signature.'
    + '<div style="padding-top:14px">Complétez les informations suivantes :</div>'
    + detailVentilation(libelles.map((libelle) => `<strong>${libelle}</strong> *`))
    + '<div style="padding-top:14px">Ces informations seront enregistrées dans la fiche de la société'
    + ' et réutilisées pour les contrats concernés.</div>';
  dialog.buttons.push(BOUTON_ANNULER);
  dialog.buttons.push(BOUTON_CONTINUER);

  // Même prudence que la confirmation DPAE : `openAlert` est posé par App.vue, et SectionAlert mappe
  // Entrée et Échap sur des libellés qui ne sont pas les nôtres. Seul « Continuer » vaut accord.
  if (!openAlert) return false;
  return (await openAlert(dialog)) == BOUTON_CONTINUER;
}

/**
 * Message bloquant, pour un manque que l'utilisateur ne peut pas compléter lui-même. Seul cas à ce
 * jour : la TVA d'une société non associative, que la section identité n'affiche pas (plan, §6.1).
 */
async function signalerTvaIncompletable(societe?: string): Promise<void> {
  const dialog = new Dialog();
  dialog.label = titreModale('Informations société à compléter')
    + 'Le régime de TVA de la société'
    + (societe ? ` <strong>${societe}</strong>` : '')
    + ' n\'est pas renseigné. Contactez le support.<br>'
    + 'Les contrats de cette société ne peuvent pas être envoyés en signature.';
  await Alerts.message(dialog);
}

/**
 * Ouvre les modales du parcours unitaire, l'une après l'autre : identité d'abord, signataire ensuite,
 * dans l'ordre où le parcours unitaire les rencontre (calcul du contrat, puis soumission).
 */
async function ouvrirModalesSociete(etablissement: Etablissement, codes: ControleSignatureMasseManqueCodes[]): Promise<void> {
  if (codes.some((code) => MANQUES_IDENTITE.includes(code))) {
    await ouvrirEtAttendre(routeModale(Modules.ETABLISSEMENT, Fiches.ETABLISSEMENT,
      Sections.ETABLISSEMENT_IDENTITE, etablissement.id));
  }

  if (codes.includes(MANQUE_SIGNATAIRE_ABSENT)) {
    // Pas de contact à éditer : on ouvre les contacts de l'établissement, comme `Submit` et la
    // validation de paie (`PaieValid.SocieteContactToSet`) le font dans ce cas.
    await ouvrirEtAttendre(routeModale(Modules.ETABLISSEMENT, Fiches.ETABLISSEMENT,
      Sections.ETABLISSEMENT_CONTACTS, etablissement.id));
  } else if (codes.includes(MANQUE_SIGNATAIRE_INCOMPLET) && etablissement.representantId > 0) {
    const route = routeModale(Modules.PROFIL, Fiches.NONE, Sections.CONTACT, etablissement.representantId);
    route.requiredAttributs = ATTRIBUTS_SIGNATAIRE;
    await ouvrirEtAttendre(route);
  }
}

/**
 * Crée l'étape société. Une instance par parcours : elle garde le dernier contrôle serveur entre
 * `controler` et `completer`. `source` : contrôle partagé du parcours.
 */
export function creerEtapeSociete(source: SourceControle): EtapeControle {
  let manquesServeur: ControleSignatureMasseManque[] = [];

  /** Manques de société, regroupés par établissement. */
  const parEtablissement = (): Map<number, ControleSignatureMasseManque[]> => {
    const groupes = new Map<number, ControleSignatureMasseManque[]>();
    manquesServeur
      .filter((manque) => manque.etape == ETAPE_SOCIETE)
      .forEach((manque) => {
        groupes.set(manque.etablissementId, [...(groupes.get(manque.etablissementId) ?? []), manque]);
      });
    return groupes;
  };

  return {
    code: 'SOCIETE',

    async charger(contrats: Contrat[]): Promise<void> {
      manquesServeur = await source.manques(contrats);
    },

    controler(): Manque[] {
      return manquesServeur
        .filter((manque) => manque.etape == ETAPE_SOCIETE || MANQUES_PERIMETRE.includes(manque.code))
        .map((manque) => ({ code: manque.code, libelle: manque.libelle, contratIds: manque.contratIds }));
    },

    async completer(contrats: Contrat[]): Promise<boolean> {
      const groupes = parEtablissement();
      const plusieurs = new Set(contrats.map((contrat) => contrat.etablissementId)).size > 1;
      let aCompleter = false;

      for (const [etablissementId, manques] of groupes) {
        // Relue pour le nom (messages multi-sociétés) et l'id du représentant (modale contact).
        const etablissement = await Etablissement.get(etablissementId);
        const societe = plusieurs ? nomSociete(etablissement) : undefined;

        // Une société bloquée sans recours n'est pas proposée à la complétion : compléter le reste
        // ne rendrait pas ses contrats envoyables.
        if (manques.some((manque) => !manque.completable)) {
          await signalerTvaIncompletable(societe);
          continue;
        }

        const nombreContrats = contrats.filter((contrat) => contrat.etablissementId == etablissementId).length;
        if (!await proposerCompletion(nombreContrats, manques.map((manque) => manque.libelle), societe)) {
          // Annulation : on s'arrête là, sans enchaîner sur les autres sociétés.
          return aCompleter;
        }
        await ouvrirModalesSociete(etablissement, manques.map((manque) => manque.code));
        aCompleter = true;
      }
      return aCompleter;
    },
  };
}
